import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Stack, Input, Grid, Card, Chip, Avatar } from '@mui/joy';
import { supabase } from '../supabaseClient';
import PageLayout from '../components/PageLayout';
import { Link as RouterLink } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import MetricCard from '../components/MetricCard';
import EnhancedButton from '../components/EnhancedButton';
import EnhancedInput from '../components/EnhancedInput';
import LoadingSkeleton, { CardSkeleton } from '../components/LoadingSkeleton';

const GISProjects = () => {
  const [projectsByClient, setProjectsByClient] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [allProjects, setAllProjects] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('gis_projects')
        .select('id, name, client_id, description, external_map_url, clients(name), created_at')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching projects:', error);
        setLoading(false);
        return;
      }

      setAllProjects(data || []);

      const grouped = {};
      data.forEach((project) => {
        const clientName = project.clients?.name || 'Unassigned';
        if (!grouped[clientName]) grouped[clientName] = [];
        grouped[clientName].push(project);
      });

      setProjectsByClient(grouped);
      setLoading(false);
    };

    fetchProjects();
  }, []);

  const allClients = Object.keys(projectsByClient);
  const totalProjects = allProjects.length;
  const totalClients = allClients.length;
  const recentProjects = allProjects.filter(p => {
    const createdDate = new Date(p.created_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return createdDate > thirtyDaysAgo;
  }).length;

  const filterProjects = (projects) => {
    return projects.filter(p =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase())
    );
  };

  const getClientInitials = (clientName) => {
    return clientName.split(' ').map(word => word[0]).join('').toUpperCase();
  };

  const getClientColor = (clientName) => {
    const colors = ['#00A884', '#007E33', '#17a2b8', '#6c757d', '#28a745'];
    const index = clientName.length % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <PageLayout title="GIS Projects" fullWidth>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
          {/* Analytics Loading */}
          <Box sx={{ mb: 4 }}>
            <LoadingSkeleton height="40px" width="300px" sx={{ mb: 3 }} />
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid xs={12} sm={6} md={3}>
                <CardSkeleton />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <CardSkeleton />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <CardSkeleton />
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <CardSkeleton />
              </Grid>
            </Grid>
          </Box>
          
          {/* Search Loading */}
          <CardSkeleton />
          
          {/* Projects Loading */}
          <Box sx={{ mt: 4 }}>
            <LoadingSkeleton count={6} spacing={2} />
          </Box>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="GIS Projects" fullWidth>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
        {/* Analytics Overview */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            level="h3" 
            sx={{ 
              mb: 3, 
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
            className="fade-in"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
              map
            </span>
            GIS Projects Overview
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
            <Grid xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Projects"
                value={totalProjects}
                icon="location_on"
                gradientColors={['#00A884', '#007E33']}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <MetricCard
                title="Active Clients"
                value={totalClients}
                icon="business"
                gradientColors={['#17a2b8', '#138496']}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <MetricCard
                title="Recent Projects"
                value={recentProjects}
                icon="schedule"
                gradientColors={['#28a745', '#218838']}
              />
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <MetricCard
                title="Map Views"
                value="∞"
                icon="visibility"
                gradientColors={['#6c757d', '#5a6268']}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Search and Filters */}
        <Card 
          sx={{ 
            p: 3, 
            mb: 4,
            background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
          className="fade-in"
        >
          <Typography 
            level="h4" 
            sx={{ 
              mb: 2, 
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              search
            </span>
            Search & Filter
          </Typography>
          
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start">
            <EnhancedInput
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                  search
                </span>
              }
              placeholder="Search projects and descriptions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: '100%', sm: 400 } }}
            />
          </Stack>
        </Card>

        {/* Projects by Client */}
        <Box className="fade-in-up">
          {allClients.map(client => {
            const filteredProjects = filterProjects(projectsByClient[client]);
            if (filteredProjects.length === 0) return null;

            return (
              <Card 
                key={client} 
                sx={{ 
                  mb: 4, 
                  p: 0,
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid',
                  borderColor: 'neutral.200',
                }}
                className="hover-lift"
              >
                {/* Client Header */}
                <Box 
                  sx={{ 
                    p: 3, 
                    background: `linear-gradient(135deg, ${getClientColor(client)}15 0%, ${getClientColor(client)}05 100%)`,
                    borderBottom: '1px solid',
                    borderColor: 'neutral.200',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: getClientColor(client),
                        color: 'white',
                        fontWeight: 700
                      }}
                    >
                      {getClientInitials(client)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography level="h4" sx={{ color: 'success.700', mb: 0.5 }}>
                        {client}
                      </Typography>
                      <Chip 
                        size="sm" 
                        variant="soft" 
                        color="success"
                        startDecorator={
                          <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                            folder
                          </span>
                        }
                      >
                        {filteredProjects.length} Project{filteredProjects.length !== 1 ? 's' : ''}
                      </Chip>
                    </Box>
                  </Box>
                </Box>

                {/* Projects Grid */}
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {filteredProjects.map(project => (
                      <Grid key={project.id} xs={12} sm={6} md={4}>
                        <Card 
                          sx={{ 
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 8px 25px rgba(0, 168, 132, 0.15)',
                            }
                          }}
                          component={RouterLink}
                          to={`/gis-projects/${project.id}`}
                        >
                          <Box 
                            sx={{ 
                              height: '120px',
                              background: `linear-gradient(45deg, ${getClientColor(client)}40, ${getClientColor(client)}20)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                          >
                            <span 
                              className="material-symbols-outlined" 
                              style={{ 
                                fontSize: '48px', 
                                color: getClientColor(client),
                                fontFamily: 'Material Symbols Outlined'
                              }}
                            >
                              map
                            </span>
                            <Chip
                              size="sm"
                              variant="solid"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'success.500',
                                color: 'white'
                              }}
                              startDecorator={
                                <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                                  launch
                                </span>
                              }
                            >
                              Interactive
                            </Chip>
                          </Box>
                          
                          <Box sx={{ p: 2 }}>
                            <Typography level="title-sm" sx={{ mb: 1, color: 'success.700' }}>
                              {project.name || 'Untitled Project'}
                            </Typography>
                            <Typography 
                              level="body-sm" 
                              sx={{ 
                                color: 'text.secondary',
                                mb: 2,
                                minHeight: '40px',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {project.description || 'No description available'}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                Created {new Date(project.created_at).toLocaleDateString()}
                              </Typography>
                              <EnhancedButton
                                size="sm"
                                variant="solid"
                                gradient
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.location.href = `/gis-projects/${project.id}`;
                                }}
                                startDecorator={
                                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                                    open_in_new
                                  </span>
                                }
                              >
                                View Map
                              </EnhancedButton>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Card>
            );
          })}
          
          {allClients.length === 0 && (
            <Card sx={{ p: 6, textAlign: 'center' }}>
              <Typography level="h4" sx={{ mb: 2, color: 'neutral.500' }}>
                No GIS Projects Found
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Projects will appear here once they are created
              </Typography>
            </Card>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
};

export default GISProjects;